import dayjs from 'dayjs';
import moment from 'moment';
import momentTZ from 'moment-timezone';

export const getDaysDurationFromCurrentDate = (date: string) => {
  if (date) {
    const dateLocal = new Date(date);
    const newDate = new Date(dateLocal.getTime() - dateLocal.getTimezoneOffset() * 60 * 1000);
    const localDate = new Date();
    const diffTime = Math.abs(localDate.getTime() - newDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 3600 * 24));
    return `${diffDays} Days`;
  }
  return '0 Days';
};

export const getDateFromDaysAgo = (daysAgo: string | number | undefined) => {
  const daysAgoNumber = daysAgo !== undefined ? parseInt(daysAgo.toString(), 10) : 0;

  if (isNaN(daysAgoNumber)) {
    throw new Error('daysAgo must be a valid number');
  }

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - daysAgoNumber);
  currentDate.setHours(0, 0, 0, 0);

  return currentDate.toISOString().split('T')[0];
};


export const getFirstMondayDate = (year: number, weekNumber: number) => {
  const firstMonday = moment().year(year).week(weekNumber).day(1);
  return firstMonday.toDate();
};

export const convertDate = (isoDateString: string) => {
  if (!isoDateString) {
    return null;
  }
  const date = new Date(isoDateString);
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString(undefined, options);
}

export const formatDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return `${day} ${monthNames[month]} ${year}`;
}

export const extractDate = (date: string) => {
  return dayjs(date)?.format('YYYY-MM-DD') || '-';
};

export const getLocalDate = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateStr = new Date().toLocaleString('en-US', { timeZone });
  return dayjs(dateStr);  // Convert to Day.js object
};

export const convertToLocalDate = (inputDate: Date) => {
  let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Fallback to New Zealand time if timeZone is undefined
  if (!timeZone) {
    console.warn('Unable to determine local time zone. Falling back to Pacific/Auckland.');
    timeZone = 'Pacific/Auckland';
  }

  return momentTZ(inputDate).tz(timeZone);
};

export const convertDateAndTime = (isoDateString: string): string => {
    const date = new Date(isoDateString);

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0'); // Ensure hours is always two digits
    const minutes = String(date.getMinutes()).padStart(2, '0'); // Ensure minutes is always two digits

    const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return `${hours}:${minutes}, ${day} ${monthNames[month]} ${year}`;
}

export const calculateTimeLeft = (closingDate: string) => {
  const now = getLocalDate().toDate();
  const closing = convertToLocalDate(new Date(closingDate)).toDate();
  const difference = closing.getTime() - now.getTime();

  if (difference > 0) {
    const hours = Math.floor(difference / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return `${hours} hours ${minutes} mins ${seconds} seconds left`;
  } else {
    return null;
  }
};

export const formatUTCDate = (date: Date) => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dd = String(utcDate.getUTCDate()).padStart(2, '0');
  const mm = String(utcDate.getUTCMonth() + 1).padStart(2, '0'); // January is 0!
  const yyyy = utcDate.getUTCFullYear();

  return `${yyyy}-${mm}-${dd}`;
};

export const convertDateToUTC = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // months are 0-based
    const day = date.getUTCDate().toString().padStart(2, '0');
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const seconds = date.getUTCSeconds().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

export const stringToUTC = (dateString: string | null) => {
  if (!dateString) return null;
  const dateObj = new Date(dateString);
  return dateObj.toISOString();
};

export const stringToDate = (dateString: string | null): dayjs.Dayjs | null => {
  if (!dateString) return null;
  return dayjs(dateString);
};